import { useCallback, useRef, useState, type DragEvent } from 'react';
import { AlertTriangle, ImageIcon, Play, Search, Upload, X } from 'lucide-react';
import { cn, formatBytes } from '@/lib/cn';
import { useT } from '@/i18n';
import { useMedia, useUploadMedia } from '@/lib/queries';
import { toApiError } from '@/lib/api';
import type { MediaItem } from '@/lib/types';
import { Modal } from './ui/primitives';
import { Button } from './ui/Button';
import { Input } from './ui/Field';
import { EmptyState, Skeleton } from './ui/Feedback';

/**
 * Drag-and-drop anywhere in the picker, per-file progress, and a per-file
 * error that names the fix (§10.5). Failures stay in the list with a retry —
 * they do not vanish.
 *
 * This is where the "Unexpected token '<'" class of error must never reach the
 * user: the backend guarantees a JSON body for every rejection, and this
 * component renders `error.message` directly because that message is already
 * written for a person.
 */

interface PendingUpload {
  id: string;
  file: File;
  state: 'uploading' | 'error';
  message?: string;
}

/** A file is a video when its mime type starts with `video/`. */
const isVideoMedia = (media: MediaItem) => media.mime.startsWith('video/');

export function MediaPicker({
  open,
  onOpenChange,
  onPick,
  allowVideo = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (media: MediaItem) => void;
  /**
   * Opt-in video support. Off by default so the existing image-only callers
   * (cover, OG image) keep filtering videos out and only accept images on
   * upload. Story slides pass `allowVideo` to also accept mp4.
   */
  allowVideo?: boolean;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unused'>('all');
  const { data, isLoading } = useMedia({ q: query || undefined, filter });
  // Image-only callers must never be offered a video to pick as a cover.
  const items = data ? (allowVideo ? data.items : data.items.filter((m) => !isVideoMedia(m))) : [];

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={t.media.title} width="lg">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[12rem] flex-1">
            <Search aria-hidden className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t.common.search}
              className="pl-9"
              aria-label={t.common.search}
            />
          </div>
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: t.media.filterAll },
              { value: 'unused', label: t.media.filterUnused },
            ]}
          />
        </div>

        <UploadZone allowVideo={allowVideo} />

        {isLoading ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="aspect-[4/3] rounded-card" />
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {items.map((item) => (
              <MediaCard key={item.id} media={item} onPick={() => onPick(item)} />
            ))}
          </div>
        ) : (
          <EmptyState icon={<ImageIcon className="h-6 w-6" />} title={t.media.emptyTitle} hint={t.media.emptyHint} />
        )}
      </div>
    </Modal>
  );
}

export function MediaCard({
  media,
  onPick,
  selected,
}: {
  media: MediaItem;
  onPick: () => void;
  selected?: boolean;
}) {
  const t = useT();
  const alt = media.alt.uz ?? media.alt.ru ?? media.alt.en;
  const video = isVideoMedia(media);
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        'group flex cursor-pointer flex-col overflow-hidden rounded-card border bg-surface text-left',
        'transition-ui duration-quick ease-out hover:-translate-y-px',
        selected
          ? 'border-brand-500 shadow-seg ring-1 ring-brand-500/30'
          : 'border-line hover:border-line-strong hover:shadow-card',
      )}
    >
      <span className="relative block aspect-[4/3] w-full overflow-hidden bg-line/50">
        {video ? (
          <video src={media.url} muted playsInline preload="metadata" className="h-full w-full object-cover" />
        ) : (
          <img src={media.url} alt="" loading="lazy" className="h-full w-full object-cover" />
        )}
        {video && (
          <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 rounded-chip bg-ink-900/70 px-1.5 py-0.5 text-[0.6875rem] font-medium text-white">
            <Play aria-hidden className="h-3 w-3" />
            {t.media.video}
          </span>
        )}
      </span>
      <span className="flex flex-col gap-0.5 border-t border-line px-2.5 py-2">
        <span className="truncate text-ui text-strong">{media.original}</span>
        <span className="flex items-center gap-1.5 font-mono text-[0.6875rem] text-faint">
          {video ? formatBytes(media.size) : `${media.width}×${media.height} · ${formatBytes(media.size)}`}
        </span>
        {/* Empty alt on a cover is a publish warning, so surface it here too. */}
        {!video && !alt && (
          <span className="mt-0.5 inline-flex items-center gap-1 text-[0.6875rem] text-amber-500">
            <AlertTriangle aria-hidden className="h-3 w-3" />
            {t.editor.altMissing}
          </span>
        )}
      </span>
    </button>
  );
}

function UploadZone({ allowVideo = false }: { allowVideo?: boolean }) {
  const t = useT();
  const upload = useUploadMedia();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [pending, setPending] = useState<PendingUpload[]>([]);

  const send = useCallback(
    (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        const id = `${file.name}-${Math.random().toString(36).slice(2, 8)}`;
        setPending((current) => [...current, { id, file, state: 'uploading' }]);
        upload
          .mutateAsync(file)
          .then(() => setPending((current) => current.filter((p) => p.id !== id)))
          .catch((error: unknown) => {
            const api = toApiError(error);
            setPending((current) =>
              current.map((p) =>
                p.id === id
                  ? {
                      ...p,
                      state: 'error',
                      // The server's message is already written for a person
                      // and names the fix. Rendering our own would be worse.
                      message:
                        api.message === '__NETWORK__' ? t.errors.network : api.message,
                    }
                  : p,
              ),
            );
          });
      }
    },
    [upload, t.errors.network],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length > 0) send(event.dataTransfer.files);
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-card border border-dashed px-4 py-6 text-center',
          'transition-ui duration-instant ease-out',
          dragging ? 'border-brand-500 bg-brand-500/6' : 'border-line hover:border-line-strong',
        )}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
      >
        <Upload aria-hidden className="h-5 w-5 text-brand-500" />
        <p className="text-ui font-medium text-strong">{t.media.dropHere}</p>
        <p className="text-ui text-muted">{allowVideo ? t.media.dropHintVideo : t.media.dropHint}</p>
        <input
          ref={inputRef}
          type="file"
          accept={allowVideo ? 'image/jpeg,image/png,image/webp,image/avif,video/mp4' : 'image/jpeg,image/png,image/webp,image/avif'}
          multiple
          className="sr-only"
          onChange={(event) => {
            if (event.target.files) send(event.target.files);
            event.target.value = '';
          }}
        />
      </div>

      <>
        {pending.map((item) => (
          <div
            key={item.id}
            className={cn(
              'anim-enter flex items-center justify-between gap-3 rounded-control border px-3 py-2 text-ui',
              item.state === 'error' ? 'border-accent-500/40 bg-accent-50 dark:bg-accent-500/10' : 'border-line bg-raised',
            )}
          >
            <div className="min-w-0">
              <p className="truncate font-medium text-strong">{item.file.name}</p>
              {item.state === 'error' ? (
                <p className="text-accent-600 dark:text-accent-400">{item.message}</p>
              ) : (
                <p className="text-muted">{t.media.uploading}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {item.state === 'error' && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setPending((current) => current.filter((p) => p.id !== item.id));
                    send([item.file]);
                  }}
                >
                  {t.common.retry}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                aria-label={t.common.close}
                onClick={() => setPending((current) => current.filter((p) => p.id !== item.id))}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </>
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: string; badge?: number }>;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-control border border-line bg-raised p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'flex cursor-pointer items-center gap-1.5 rounded-inset px-3 py-1.5 text-ui font-medium',
            'transition-ui duration-instant ease-out',
            // Selected = a real raised surface with a hairline, not a drop
            // shadow. The previous `shadow-sm` read as smudge at this size.
            value === option.value
              ? 'bg-surface text-strong shadow-seg ring-1 ring-line-strong'
              : 'text-muted hover:bg-surface/60 hover:text-strong',
          )}
        >
          {option.label}
          {option.badge !== undefined && option.badge > 0 && (
            <span className="rounded-chip bg-brand-500/12 px-1.5 font-mono text-[0.6875rem] text-brand-600 dark:text-brand-300">
              {option.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
